// Imports
import { ArrowRightIcon } from '@heroicons/react/20/solid';
import { Controller } from 'react-hook-form';
import PhoneInput, { type FlagProps } from 'react-phone-number-input';

import Checkbox from '@/components/Forms/Checkbox';
import FormControl from '@/components/Forms/FormControl';
import FormErrors from '@/components/Forms/FormErrors';
import FormSubmitButton from '@/components/Forms/FormSubmitButton';
import InnerTextInput from '@/components/Forms/InnerTextInput';
import PasswordInput from '@/components/Forms/PasswordInput';
import getObjectKeysUnsafe from '@/renderer/utils/getObjectKeysUnsafe';
import useClientInteractive from '@/renderer/utils/useInteractive';
import useRecaptcha from '@/renderer/utils/useRecaptcha';
import type { SubFormProps } from '@/renderer/utils/useRequest';

import { GlobeAltIcon, PhoneIcon } from '@heroicons/react/16/solid';
import type { StepOneData } from './types';

export default StepOne;

// Helper functions
const isEmail = (value: string) => /\S+@\S+\.\S+/.test(value);
const isPhone = (value: string) => /^\+?\d{10,15}$/.test(value);

function validateStepOne<V extends StepOneData>(data: V) {
  const errors: Partial<Record<keyof V, string>> = {};

  if (!data.firstName) {
    errors.firstName = 'First name is required';
  }

  if (!data.lastName) {
    errors.lastName = 'Last name is required';
  }

  if (!data.email) {
    errors.email = 'Email is required';
  } else if (!isEmail(data.email)) {
    errors.email = 'Invalid email format';
  }

  if (data.contactSecondary && !isPhone(data.contactSecondary)) {
    errors.contactSecondary = 'Invalid phone format';
  }

  if (data.contactSecondary && !data.acceptSms) {
    errors.acceptSms = 'You must accept terms if you provide a phone number';
  }

  if (data.password?.length < 8) {
    errors.password = 'Password must be at least 8 characters long';
  }

  return errors;
}

const flagStyles = {
  display: 'inline-block',
  position: 'relative',
  maxHeight: '24px',
} as const;

const flagPhoneStyles = {
  display: 'inline-block',
  position: 'absolute',
  left: '-1px',
  opacity: '0.5',
  rotate: '45deg',
} as const;

const flagGlobeStyles = {
  display: 'inline-block',
  position: 'absolute',
  right: '-1px',
  opacity: '0.5',
} as const;

function Flag(props: FlagProps) {
  const Component = props?.flags?.[props?.country];

  if (Component) {
    return (
      <span style={flagStyles} className="PhoneInputCountryIconImg">
        <Component title={props.countryName} />
      </span>
    );
  }

  if (props?.flagUrl) {
    return (
      <img
        height={12}
        className="PhoneInputCountryIconImg"
        src={props.flagUrl.replace('{XX}', props.country)}
        alt={props.countryName}
      />
    );
  }

  return (
    <span style={flagStyles} className="PhoneInputCountryIconImg">
      <PhoneIcon height={16} style={flagPhoneStyles} />
      <GlobeAltIcon
        height={16}
        style={flagGlobeStyles}
        title={props?.countryName || 'International'}
      />
    </span>
  );
}

// Step one component
function StepOne({ form, onSubmit, request }: SubFormProps<StepOneData>) {
  const { register, handleSubmit } = form;

  // As this page uses reCAPTCHA, we can't let them submit the form straight to server without JavaScript
  const clientSideInteractive = useClientInteractive();
  const { loading: recaptchaLoading, generateToken: generateRecaptchaToken } = useRecaptcha();

  const handleFormSubmit = async (data: StepOneData) => {
    const errs = validateStepOne(data);

    if (Object.keys(errs).length > 0) {
      for (const key of getObjectKeysUnsafe(errs)) {
        form.setError(key, {
          type: 'manual',
          message: errs[key],
        });
      }

      return;
    }

    let recaptcha = '';

    try {
      recaptcha = await generateRecaptchaToken('submit');
    } catch (error) {
      form.setError('recaptcha', {
        type: 'manual',
        message: error instanceof Error ? error.message : String(error),
      });

      return;
    }

    if (!recaptcha) {
      form.setError('recaptcha', {
        type: 'manual',
        message: 'Failed to load CAPTCHA code. Please refresh or contact a site administrator.',
      });
    }

    await onSubmit({ ...data, recaptcha });
  };

  return (
    <>
      <div className="space-y-5">
        <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-200">
          Sign up and get free plays
        </h1>
        <p className="text-sm text-zinc-500 dark:text-zinc-400">
          Get access to our free releases just by creating an account. No credit card required.
        </p>
      </div>
      <form
        onSubmit={handleSubmit(handleFormSubmit, async () => {
          await form.clearErrors();
          handleSubmit(handleFormSubmit)();
        })}
        method="POST"
        className="space-y-6"
      >
        <FormControl
          form={form}
          name="firstName"
          label="First name*"
          autoComplete="given-name"
          autoFocus
          required
        />
        <FormControl
          form={form}
          name="lastName"
          label="Last name*"
          autoComplete="family-name"
          required
        />
        <FormControl
          form={form}
          name="email"
          label="Email*"
          autoComplete="email"
          required
          autoCapitalize="off"
          autoCorrect="off"
          autoSave="off"
        />
        <FormControl
          form={form}
          name="password"
          label="Password*"
          aria-autocomplete="list"
          autoComplete="new-password"
          wrap={PasswordInput}
          minLength={8}
          required
          autoCapitalize="off"
          autoCorrect="off"
          autoSave="off"
        />
        <FormControl form={form} name="contactSecondary" label="Mobile phone number">
          {/* <div className="relative rounded-sm shadow-xs"> */}
          <Controller
            render={({ field }) => (
              <PhoneInput
                inputComponent={InnerTextInput}
                inputRef={register}
                id="contactSecondary"
                name="contactSecondary"
                autoComplete="tel"
                defaultCountry="US"
                onChange={field.onChange}
                className="relative"
                autoCapitalize="off"
                autoCorrect="off"
                autoSave="off"
                flagComponent={Flag}
                internationalIcon={Flag}
              />
            )}
            control={form.control}
            name="contactSecondary"
          />
          {/* </div> */}
        </FormControl>
        <FormControl
          form={form}
          name="acceptSms"
          label={
            <>
              I would like to receive RAS’s alerts for free and/or paid plays, service announcements
              and promotions at the phone number provided. Msg freq. varies. Msg & data rates may
              apply. Reply HELP for help or STOP to cancel. I have read the{' '}
              {/* biome-ignore lint/a11y/noBlankTarget: */}
              <a href="/terms" target="_blank">
                RAS Terms of Service
              </a>{' '}
              and {/* biome-ignore lint/a11y/noBlankTarget: */}
              <a href="/privacy" target="_blank">
                Privacy Policy
              </a>
              .
            </>
          }
          wrap={Checkbox}
          inline
        />
        {/* <FormControl
          form={form}
          name="acceptPromo"
          label={
            <>
              OPTIONAL: I would like to receive RAS’s updates about new services and promotions at
              the phone number provided. Msg freq. varies. Msg & data rates may apply. Reply HELP
              for help or STOP to cancel. I have read the{' '}
              <a href="/terms" target="_blank">
                RAS Terms of Service
              </a>{' '}
              and{' '}
              <a href="/privacy" target="_blank">
                Privacy Policy
              </a>
              .
            </>
          }
          wrap={Checkbox}
          inline
        /> */}

        <FormErrors form={form} request={request} />

        <div>
          <FormSubmitButton
            disabled={request.loading || !clientSideInteractive || recaptchaLoading}
            loading={request.loading || !clientSideInteractive || recaptchaLoading}
            className="w-full"
          >
            {!clientSideInteractive ? 'Please wait' : "Let's get started"}
          </FormSubmitButton>
        </div>

        <noscript>
          <div className="bg-yellow-50 p-4">
            <div className="flex flex-row items-center space-x-2 text-sm text-yellow-700">
              <span>Please enable Javascript to register.</span>
            </div>
          </div>
        </noscript>
        <div>
          <div className="mb-8 flex items-center justify-start space-x-2 self-center text-zinc-700 dark:text-zinc-300">
            <span className="text-sm">Already have an account?</span>
            <a
              href="/signin"
              className="flex flex-row items-center space-x-1 text-sm font-semibold"
            >
              <span>Sign in </span>
              <ArrowRightIcon height={16} className="h-4 w-4" />
            </a>
          </div>
          <div className="text-xs text-zinc-500 dark:text-zinc-400">
            By creating an account you agree with our <a href="/terms">Terms of Service</a>,{' '}
            <a href="/privacy">Privacy Policy</a>, and our default{' '}
            <a href="/notifications">Notification Settings</a>.
          </div>
        </div>
      </form>
    </>
  );
}
